import React, { useState, useEffect, useContext } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTruck, faCheckCircle, faSave, faPaperPlane, faUserShield, faExchangeAlt, faColumns } from '@fortawesome/free-solid-svg-icons';
import Layout from '../../components/Layout';

import Footer from '../../components/ui/Footer';
import PurchaseOrderScreen1 from './PurchaseOrderScreen1';
import PurchaseOrderScreen2 from './PurchaseOrderScreen2';
import PurchaseOrderScreen3 from './PurchaseOrderScreen3';
import invoicingService from '../../services/invoicingService';
import { useFlashMessage } from '../../components/FlashMessage';
import InvoiceValidationModal from '../../components/Invoicing/InvoiceValidationModal';
import FinancialApprovalModal from '../../components/Invoicing/FinancialApprovalModal';
import ConversionApprovalModal from '../../components/Invoicing/ConversionApprovalModal';
import { AuthContext } from '../../context/AuthContext';
import DeliveryLinkBadge from '../../components/Invoicing/DeliveryLinkBadge';
import HMSPageHeader from '../../components/ui/HMSPageHeader';
import HMSStepper from '../../components/ui/HMSStepper';
import DocumentsSplitPane from '../../components/DocumentsSplitPane';
import RelatedDocumentsPane, { useInvoicePapersOpen } from '../../components/RelatedDocumentsPane';
import useDocumentReturn from '../../components/shared/useDocumentReturn';
import CrmMatchFlags from '../../components/Invoicing/CrmMatchFlags';
import './OutgoingInvoice.css';
import { extractApiError } from '../../utils/errorMessages';
import { linesAwaitingVat, vatFiguresToSave } from '../../components/Invoicing/utils/vatPercentage';
import PinToDeskButton from '../../components/mydesk/PinToDeskButton';

// A line whose VAT still shows "—" on Screen 2 (its rate's percentage has not
// loaded). The PO server stores the VAT and gross the screen sends, so saving
// it would store VAT 0 and gross 0: refuse, and say which line.
const vatNotLoadedMessage = (lineNumber) =>
  `Line ${lineNumber}'s VAT rate hasn't loaded yet — wait a moment, or pick the rate again, then save.`;

/**
 * Create/Edit Purchase Order - 3-Screen Flow Orchestrator
 * 
 * Manages:
 * - State for all 3 screens
 * - Navigation between screens
 * - Save draft functionality
 * - Submit for approval workflow
 * - Editing existing purchase orders
 * 
 * Purpose:
 * - Order goods/services from suppliers
 * - Track quantities ordered vs received
 * - Can be converted to incoming invoice when goods received
 */

const CreatePurchaseOrder = () => {
  const history = useHistory();
  const { showSuccess, showError } = useFlashMessage();
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  
  // State management
  const [currentScreen, setCurrentScreen] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(!!id);
  // The id of the document this screen is editing -- from the URL when we
  // arrived on one, or adopted from the server the moment a draft is first
  // created. Held here so a second Save Draft UPDATES the document instead of
  // POSTing a duplicate.
  //
  // isEditMode used to be useState(!!id), and setIsEditMode was never called
  // anywhere in five of the six creators, so the flag was frozen at mount. The
  // old redirect-to-the-list hid that: you left the screen before you could
  // press Save Draft twice. Now that saving keeps you here, the flag has to
  // follow the document.
  const [createdId, setCreatedId] = useState(null);
  const documentId = id || createdId;
  const isEditMode = Boolean(documentId);
  // Invoice Papers: the delivery's papers and contracts BESIDE the purchase order, so an approver checks one
  // against the other without leaving the screen (ClickUp 2026-10-23,
  // "double screen ... where there are approvals"). Remembered per browser,
  // shared with the invoice screens.
  const invoicePapers = useInvoicePapersOpen();
  // Who RAISED this document. Dropped by this loader until 2026-08-26, which
  // left Screen 1 showing whoever was logged in instead.
  const [documentIssuerName, setDocumentIssuerName] = useState(null);
  
  // Validation modal state
  const [showDraftValidationModal, setShowDraftValidationModal] = useState(false);
  const [draftValidationErrors, setDraftValidationErrors] = useState([]);
  const [targetScreenForValidation, setTargetScreenForValidation] = useState(null);
  
  // Approval and conversion modal state
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showConversionModal, setShowConversionModal] = useState(false);
  const [poStatus, setPoStatus] = useState(null);
  
  // Determine if PO is locked (read-only) based on status
  const isReadOnly = poStatus && !['DRAFT', 'PENDING_APPROVAL', 'REJECTED'].includes(poStatus);
  
  // Load existing purchase order if editing
  useEffect(() => {
    const loadPurchaseOrder = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        const data = await invoicingService.fetchPurchaseOrder(id);
        setDocumentIssuerName(data.created_by_name || null);
        
        // Track PO status for approval/conversion workflow
        setPoStatus(data.status);
        
        // Populate PO data
        setPoData({
          po_number: data.po_number || '',
          hms_entity: data.hms_entity,
          hms_entity_name: data.hms_entity_name,
          po_date: data.po_date,
          expected_delivery_date: data.expected_delivery_date,
          supplier: data.supplier,
          supplier_name: data.supplier_name,
          delivery_address: data.delivery_address || {},
          payment_terms: data.payment_terms,
          currency: data.currency,
          number_format: data.number_format || 'US',
          bank_account: data.bank_account,
          cost_center: data.cost_center,
          cost_category: data.cost_category,
          reporting_month: data.reporting_month || '',
          notes: data.notes || '',
          notes_visible_on_invoice: data.notes_visible_on_invoice || false,
          source_delivery: data.source_delivery || null,
          source_delivery_number: data.source_delivery_number || null,
          net_amount: data.net_amount,
          vat_amount: data.vat_amount,
          gross_amount: data.gross_amount
        });
        
        // Populate line items - map quantity_ordered to quantity for UI
        if (data.line_items) {
          const mappedLineItems = data.line_items.map(item => ({
            ...item,
            quantity: item.quantity_ordered // PO backend uses quantity_ordered, UI uses quantity
          }));
          setLineItems(mappedLineItems);
        }
        
        // Populate vessel info
        if (data.vessel_info) {
          setVesselInfo({
            vessel_name: data.vessel_info.vessel_name || '',
            imo_number: data.vessel_info.imo_number || '',
            delivery_date: data.vessel_info.delivery_date || '',
            bl_number: data.vessel_info.bl_number || '',
            bl_date: data.vessel_info.bl_date || '',
            loading_port: data.vessel_info.loading_port || '',
            discharge_port: data.vessel_info.discharge_port || '',
            incoterms: data.vessel_info.incoterms || ''
          });
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading purchase order:', error);
        showError('Failed to load purchase order. Please try again.');
        setIsLoading(false);
        goBack();
      }
    };
    
    loadPurchaseOrder();
  }, [id]);
  
  // Load delivery data for pre-fill if deliveryId in URL params
  useEffect(() => {
    const loadDeliveryPrefill = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const deliveryId = urlParams.get('deliveryId');
      // The supplier (and its recaps) chosen on the delivery's invoice window.
      // Dropping these gave every PO to the recap header supplier with every
      // supplier's fuel on it.
      const partnerId = urlParams.get('partnerId');
      const recapIdsParam = urlParams.get('recapIds');
      const recapIds = recapIdsParam ? recapIdsParam.split(',').map((rid) => parseInt(rid, 10)) : null;
      
      // Only prefill for new POs (not editing existing)
      if (deliveryId && !id) {
        try {
          setIsLoading(true);
          console.log(`Pre-filling purchase order from delivery ${deliveryId}`);
          
          const deliveryData = await invoicingService.fetchDeliveryForPurchaseOrder(deliveryId, partnerId, recapIds);
          
          // Set PO header data
          setPoData(prev => ({
            ...prev,
            ...deliveryData.po_data,
            _prefilled_from_delivery: deliveryId,
            _prefilled_delivery_number: deliveryData.delivery_display_number
          }));
          
          // Set line items from delivery
          if (deliveryData.line_items_data?.length > 0) {
            setLineItems(deliveryData.line_items_data);
          }
          
          // Set vessel info from delivery
          if (deliveryData.vessel_info_data) {
            setVesselInfo(deliveryData.vessel_info_data);
          }
          
          // Check if any required fields are missing
          const missingFields = [];
          if (!deliveryData.po_data.hms_entity) missingFields.push('Entity');
          if (!deliveryData.po_data.supplier) missingFields.push('Supplier');
          
          const deliveryNumber = deliveryData.delivery_display_number || `#${deliveryId}`;

          // A figure the deal does not state arrives as 0 on its line. Say which.
          if (deliveryData.missing_figures?.length > 0) {
            showError(
              `The deal does not state the ${deliveryData.missing_figures.join(', ')}. ` +
              'Those lines show 0 - enter the agreed figure.',
              12000
            );
          }
          
          if (missingFields.length > 0) {
            showSuccess(
              `Data pre-filled from Delivery ${deliveryNumber}. ` +
              `Please select: ${missingFields.join(', ')}`,
              8000
            );
          } else {
            showSuccess(`Purchase order data pre-filled from Delivery ${deliveryNumber}`, 8000);
          }
          console.log('PO pre-fill complete:', deliveryData);
        } catch (error) {
          console.error('Error loading delivery for PO pre-fill:', error);
          const errorMessage = error.response?.data?.error || 'Failed to load delivery data. Please fill manually.';
          showError(errorMessage, 8000);
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    loadDeliveryPrefill();
  }, [id]);
  
  // Scroll to top on mount and screen change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [currentScreen]);
  
  // PO header data (Screen 1)
  const [poData, setPoData] = useState({
    hms_entity: null,
    po_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: null,
    supplier: null, // CRM Partner
    supplier_name: '',
    delivery_address: {},
    payment_terms: null,
    currency: 'USD',
    number_format: 'US',
    bank_account: null,
    cost_center: null,
    cost_category: null,
    reporting_month: new Date().toISOString().substring(0, 8) + '01',
    notes: '',
    notes_visible_on_invoice: false
  });

  // Where this screen hands the user back to. **2026-09-09, HMS:** *"could this
  // stay in the delivery pls"*. The exits below used to hard-code '/invoicing/purchase-orders';
  // an operator who arrived from their delivery was dropped into the Finance
  // invoice table instead of back on the job they were doing. One shared rule
  // now decides for all six invoice screens: you return to whichever screen you
  // actually came in from. See components/shared/useDocumentReturn.js.
  const { returnLabel, goBack, goBackAfterSave } = useDocumentReturn({
    listPath: '/invoicing/purchase-orders',
    listLabel: 'Purchase Orders',
    user,
    sourceDeliveryId: poData.source_delivery || poData._prefilled_from_delivery || null,
    sourceDeliveryNumber: poData.source_delivery_number || poData._prefilled_delivery_number || null,
  });

  
  // Line items (Screen 2)
  const [lineItems, setLineItems] = useState([]);

  // Vessel info (Screen 2, manual input)
  const [vesselInfo, setVesselInfo] = useState({
    vessel_name: '',
    imo_number: '',
    delivery_date: '',
    bl_number: '',
    bl_date: '',
    loading_port: '',
    discharge_port: '',
    incoterms: ''
  });

  // CRM cross-check (advisory, never blocks): before ordering from a supplier,
  // flag identity drift and non-approved KYC. A PO has no bank account or OCR
  // capture, so the bank checks self-report as skipped. Declared AFTER poData —
  // the dependency array reads it at render time.
  const [crmMatch, setCrmMatch] = useState(null);
  useEffect(() => {
    const supplierId = poData.supplier;
    if (!supplierId) {
      setCrmMatch(null);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const result = await invoicingService.checkInvoiceCrmMatch({
          partner_id: supplierId,
          supplier_name: poData.supplier_name || '',
        });
        setCrmMatch(result);
      } catch (error) {
        // The check itself failing must be visible, not silently green/absent.
        setCrmMatch({
          overall: 'warning',
          checks: [{
            check: 'identity', status: 'warning',
            detail: 'CRM check could not run. Verify supplier details manually.',
          }],
        });
      }
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poData.supplier, poData.supplier_name]);
  
  const handleSaveDraft = async (modifiedData = null) => {
    try {
      setIsSaving(true);
      
      // Use modified data from Screen3 if provided, otherwise use state
      const dataToUse = modifiedData || poData;
      
      // DRAFT VALIDATION: Only IMT Entity and Supplier required
      // Everything else is optional for work in progress
      
      console.log('=== DRAFT VALIDATION DEBUG ===');
      console.log('Full dataToUse:', dataToUse);
      console.log('hms_entity value:', dataToUse.hms_entity);
      console.log('hms_entity type:', typeof dataToUse.hms_entity);
      console.log('supplier value:', dataToUse.supplier);
      console.log('supplier type:', typeof dataToUse.supplier);
      
      // Values are stored as direct integers, not objects
      const errors = [];
      if (!dataToUse.hms_entity) {
        errors.push('Entity is required');
      }
      if (!dataToUse.supplier) {
        errors.push('Supplier (CRM Profile) is required');
      }
      
      if (errors.length > 0) {
        console.log('Validation FAILED. Errors:', errors);
        setDraftValidationErrors(errors);
        setTargetScreenForValidation(1);
        setShowDraftValidationModal(true);
        setIsSaving(false);
        return;
      }
      
      console.log('Draft validation PASSED!');

      const vatErrors = linesAwaitingVat(lineItems).map(vatNotLoadedMessage);
      if (vatErrors.length > 0) {
        setDraftValidationErrors(vatErrors);
        setTargetScreenForValidation(2);
        setShowDraftValidationModal(true);
        setIsSaving(false);
        return;
      }

      console.log('Line items before mapping:', lineItems);
      
      const mappedLineItems = lineItems.map((item, idx) => {
        const mapped = {
          line_number: item.line_number,
          item_description: item.item_description,
          product: typeof item.product === 'object' ? item.product?.id : item.product,
          quantity_ordered: item.quantity || item.quantity_ordered || 0,
          unit: typeof item.unit === 'object' ? item.unit?.id : item.unit,
          unit_price: item.unit_price || 0,
          net_amount: item.net_amount || 0,
          currency: item.currency || dataToUse.currency,
          vat_rate: typeof item.vat_rate === 'object' ? item.vat_rate?.id : item.vat_rate,
          // The figures the grid worked out (refused above while "—").
          ...vatFiguresToSave(item),
          gl_account: typeof item.gl_account === 'object' ? item.gl_account?.id : item.gl_account
        };
        console.log(`Mapped line item ${idx + 1}:`, mapped);
        return mapped;
      });
      
      const payload = {
        ...dataToUse,
        status: 'DRAFT',
        reporting_month: dataToUse.reporting_month 
          ? (dataToUse.reporting_month.length === 7 
              ? dataToUse.reporting_month + '-01'
              : dataToUse.reporting_month)
          : null,
        line_items: mappedLineItems,
        vessel_info: vesselInfo
      };
      
      console.log('Final payload:', payload);
      
      let response;
      if (documentId) {
        response = await invoicingService.updatePurchaseOrder(documentId, payload);
      } else {
        response = await invoicingService.createPurchaseOrder(payload);
      }
      
      showSuccess(
        response.po_number 
          ? `Purchase order ${isEditMode ? 'updated' : 'saved'} successfully! PO Number: ${response.po_number}`
          : `Purchase order draft ${isEditMode ? 'updated' : 'saved'} successfully! Submit for approval to generate PO number.`,
        8000
      );
      
      // Saving a draft KEEPS YOU ON THE DOCUMENT. It used to eject to the list
      // 1.5s later, which is what accounting reported as "it jumps to the
      // table" -- you are mid-edit and the screen throws you out. Only
      // Save & Workflow navigates away now, because there the document really
      // has left your hands.
      //
      // The new id is adopted into STATE, deliberately NOT into the URL:
      // /create and /edit/:id are separate <Route>s inside App.js's <Switch>,
      // so replacing the URL would unmount and remount this screen and drop the
      // user back on step 1. A smaller jump than the old one, but still a jump.
      if (!documentId && response?.id) {
        setCreatedId(response.id);
      }
      
    } catch (error) {
      console.error('Error saving draft:', error);
      const errorMessage = extractApiError(error, 'Failed to save draft. Please check all fields and try again.');
      showError(errorMessage, 8000);
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleSubmitApproval = async (modifiedData = null) => {
    try {
      setIsSaving(true);
      
      // Use modified data from Screen3 if provided, otherwise use state
      const dataToUse = modifiedData || poData;
      
      console.log('=== WORKFLOW VALIDATION DEBUG ===');
      console.log('Full dataToUse:', dataToUse);
      console.log('hms_entity value:', dataToUse.hms_entity);
      console.log('supplier value:', dataToUse.supplier);
      
      // Validate ALL required fields and collect errors
      const errors = [];
      let targetScreen = null;
      
      // Screen 1 required fields - values are direct integers, not objects
      if (!dataToUse.hms_entity) {
        errors.push('Entity is required');
        targetScreen = 1;
      }
      if (!dataToUse.po_date) {
        errors.push('PO Date is required');
        targetScreen = targetScreen || 1;
      }
      if (!dataToUse.supplier) {
        errors.push('Supplier (CRM Profile) is required');
        targetScreen = targetScreen || 1;
      }
      if (!dataToUse.payment_terms) {
        errors.push('Payment Terms is required');
        targetScreen = targetScreen || 1;
      }
      
      // Screen 2 required fields - line items validation
      if (lineItems.length === 0) {
        errors.push('At least one line item is required');
        targetScreen = targetScreen || 2;
      } else {
        // Validate each line item
        lineItems.forEach((item, index) => {
          if (!item.item_description || !item.item_description.trim()) {
            errors.push(`Line ${index + 1}: Description is required`);
            targetScreen = targetScreen || 2;
          }
          if (!item.quantity || parseFloat(item.quantity) <= 0) {
            errors.push(`Line ${index + 1}: Quantity must be greater than 0`);
            targetScreen = targetScreen || 2;
          }
          if (!item.unit_price || parseFloat(item.unit_price) <= 0) {
            errors.push(`Line ${index + 1}: Unit Price must be greater than 0`);
            targetScreen = targetScreen || 2;
          }
          if (!item.vat_rate) {
            errors.push(`Line ${index + 1}: VAT Rate is required`);
            targetScreen = targetScreen || 2;
          }
        });
        linesAwaitingVat(lineItems).forEach((lineNumber) => {
          errors.push(vatNotLoadedMessage(lineNumber));
          targetScreen = targetScreen || 2;
        });
      }
      
      // Validate total amount
      const total = parseFloat(dataToUse.gross_amount || 0);
      if (total <= 0) {
        errors.push('Purchase order total must be greater than zero');
        targetScreen = targetScreen || 2;
      }
      
      // Screen 3 required fields
      if (!dataToUse.reporting_month) {
        errors.push('Reporting Month is required');
        targetScreen = targetScreen || 3;
      }
      
      if (errors.length > 0) {
        setDraftValidationErrors(errors);
        setTargetScreenForValidation(targetScreen);
        setShowDraftValidationModal(true);
        setIsSaving(false);
        return;
      }
      
      console.log('Line items before mapping:', lineItems);
      
      const mappedLineItems = lineItems.map((item, idx) => {
        const mapped = {
          line_number: item.line_number,
          item_description: item.item_description,
          product: typeof item.product === 'object' ? item.product?.id : item.product,
          quantity_ordered: item.quantity || item.quantity_ordered || 0,
          unit: typeof item.unit === 'object' ? item.unit?.id : item.unit,
          unit_price: item.unit_price || 0,
          net_amount: item.net_amount || 0,
          currency: item.currency || dataToUse.currency,
          vat_rate: typeof item.vat_rate === 'object' ? item.vat_rate?.id : item.vat_rate,
          // The figures the grid worked out (refused above while "—").
          ...vatFiguresToSave(item),
          gl_account: typeof item.gl_account === 'object' ? item.gl_account?.id : item.gl_account
        };
        console.log(`Mapped line item ${idx + 1}:`, mapped);
        return mapped;
      });
      
      const payload = {
        ...dataToUse,
        status: 'PENDING_APPROVAL',
        reporting_month: dataToUse.reporting_month 
          ? (dataToUse.reporting_month.length === 7 
              ? dataToUse.reporting_month + '-01'
              : dataToUse.reporting_month)
          : null,
        line_items: mappedLineItems,
        vessel_info: vesselInfo
      };
      
      console.log('Final payload:', payload);
      
      let response;
      if (documentId) {
        response = await invoicingService.updatePurchaseOrder(documentId, payload);
      } else {
        response = await invoicingService.createPurchaseOrder(payload);
      }
      
      showSuccess(
        `Purchase order ${isEditMode ? 'updated and ' : ''}submitted for approval! ` +
        `PO Number: ${response.po_number}, Supplier: ${response.supplier_name}, ` +
        `Status: Pending Approval, Total Amount: ${response.currency} ${response.gross_amount}`,
        8000
      );
      
      // Redirect to PO list
      setTimeout(() => goBackAfterSave(response?.id || id), 1500);
      
    } catch (error) {
      console.error('Error submitting PO:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      
      // Extract detailed error message from backend
      let errorMessage = 'Failed to submit purchase order for approval. Please check all fields and try again.';
      
      // extractApiError names the row and the field. The old inline version
      // join()ed line_items -- a list of dicts -- into "[object Object]".
      errorMessage = extractApiError(error, errorMessage);
      
      showError(errorMessage, 10000);
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleFinancialApprovalAction = async ({ action, rejection_reason, notes }) => {
    try {
      let response;
      
      if (action === 'approve') {
        response = await invoicingService.approvePurchaseOrder(id, notes);
        const poNumber = response?.po?.po_number || poData.po_number;
        showSuccess(`${poNumber} approved successfully!`, 8000);
      } else if (action === 'reject') {
        response = await invoicingService.rejectPurchaseOrder(id, rejection_reason);
        showSuccess(`Purchase order rejected. The submitter has been notified.`, 8000);
      }
      
      const updatedPO = await invoicingService.fetchPurchaseOrder(id);
      setPoStatus(updatedPO.status);
      setPoData(prev => ({ ...prev, po_number: updatedPO.po_number }));
      
      setTimeout(() => goBackAfterSave(id), 2000);
    } catch (error) {
      console.error('Error processing approval action:', error);
      const errorMessage = error.response?.data?.error || error.message;
      showError(errorMessage, 8000);
    }
  };
  
  const handleConversionAction = async ({ action, notes, reason }) => {
    try {
      if (action === 'request_conversion') {
        await invoicingService.submitPOForConversion(id);
        showSuccess('Conversion request submitted for approval.', 8000);
        
        const updatedPO = await invoicingService.fetchPurchaseOrder(id);
        setPoStatus(updatedPO.status);
      } else if (action === 'approve_conversion') {
        const response = await invoicingService.approvePOConversion(id, notes);
        const incomingNumber = response?.incoming_invoice?.hms_expense_number || 'Incoming Invoice';
        // Coding missing: the invoice is made PENDING_APPROVAL and the server
        // says what accounting still needs, so show that instead.
        if (response?.incoming_invoice?.status === 'PENDING_APPROVAL' && response?.message) {
          showSuccess(response.message, 12000);
        } else {
          showSuccess(`PO converted! Incoming invoice ${incomingNumber} created.`, 8000);
        }
        
        // Redirect to incoming invoice
        if (response?.incoming_invoice?.id) {
          setTimeout(() => history.push(`/invoicing/incoming/${response.incoming_invoice.id}`), 2000);
        } else {
          setTimeout(() => goBackAfterSave(id), 2000);
        }
      } else if (action === 'reject_conversion') {
        await invoicingService.rejectPOConversion(id, reason);
        showSuccess('Conversion request rejected.', 8000);
        
        const updatedPO = await invoicingService.fetchPurchaseOrder(id);
        setPoStatus(updatedPO.status);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      showError(errorMessage, 8000);
    }
  };
  
  // Shared pill stepper. `completed` keeps the old visited-before meaning:
  // any screen behind the current one is ticked. Free-jump is unchanged.
  const stepperSections = [
    { id: '1', label: 'Supplier & Header', completed: currentScreen > 1 },
    { id: '2', label: 'Products', completed: currentScreen > 2 },
    { id: '3', label: 'Cost Centre & Notes', completed: currentScreen > 3 }
  ];
  
  if (isLoading) {
    return (
      <Layout showLeftNavSidebar={true} showRightSidebar={false}>
        <div className="invoice-page-container purchase-order-screen">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading purchase order...</p>
          </div>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      {/* Validation Modal */}
      <InvoiceValidationModal
        isOpen={showDraftValidationModal}
        onClose={() => setShowDraftValidationModal(false)}
        title="Complete Required Fields"
        errors={draftValidationErrors}
        onNavigate={() => {
          if (targetScreenForValidation) {
            setCurrentScreen(targetScreenForValidation);
          }
          setShowDraftValidationModal(false);
        }}
      />
      
      <div className="invoice-page-container purchase-order-screen">
        {/* The arrow goes back to wherever this screen was opened from
            (useDocumentReturn); backLabel is its "Back to ..." wording. */}
        <HMSPageHeader
          onBack={goBack}
          backLabel={returnLabel}
          title={isEditMode ? 'Edit Purchase Order' : 'Create Purchase Order'}
          kicker="Finance"
          className="invoice-header--papers"
          actions={
            <>
              {/* The delivery link rides in the actions cluster as a chip; it
                  used to sit under the old header's subtitle line. */}
              <DeliveryLinkBadge
                deliveryId={poData.source_delivery || poData._prefilled_from_delivery}
                deliveryNumber={poData.source_delivery_number || poData._prefilled_delivery_number}
              />

              {id && <PinToDeskButton entityType="purchase_order" objectId={id} />}

              {(poStatus !== 'APPROVED' || !isEditMode) && (
                <>
                  <button
                    className="btn-secondary"
                    onClick={() => handleSaveDraft()}
                    disabled={isSaving}
                    style={{ minWidth: '120px' }}
                  >
                    <FontAwesomeIcon icon={faSave} /> Save Draft
                  </button>
                  <button
                    className="btn-primary"
                    onClick={() => handleSubmitApproval()}
                    disabled={isSaving}
                    style={{ minWidth: '160px' }}
                  >
                    <FontAwesomeIcon icon={faPaperPlane} /> Save & Workflow
                  </button>
                </>
              )}

              {isEditMode && (
                <button
                  className="btn-secondary"
                  onClick={invoicePapers.toggle}
                  style={{ minWidth: '140px' }}
                  title="Show the delivery papers and contracts beside this purchase order"
                  aria-pressed={invoicePapers.isOpen}
                  data-testid="invoice-papers-toggle"
                >
                  <FontAwesomeIcon icon={faColumns} /> Invoice Papers
                </button>
              )}
            </>
          }
        />
        
        {/* Progress Indicator */}
        {/* [ Purchase Order | Invoice Papers ]: the same split as the delivery's
            Contracts & Notes. Closed, the purchase order renders exactly as before. */}
        <div className={`record-split invoice-papers-split${documentId && invoicePapers.isOpen ? ' record-split--open' : ''}`}>
        <div className="record-split-content invoice-papers-record">
        <div className="hms-stepper-bar">
          <HMSStepper
            sections={stepperSections}
            activeId={String(currentScreen)}
            onStepClick={(id) => setCurrentScreen(Number(id))}
          />
        </div>
        
        {/* Financial Approver Banner */}
        {isEditMode && poStatus === 'PENDING_APPROVAL' && user?.is_financial_approver && (
          <div className="financial-approver-banner">
            <div className="financial-approver-banner-content">
              <div className="financial-approver-banner-icon">
                <FontAwesomeIcon icon={faUserShield} />
              </div>
              <div className="financial-approver-banner-text">
                <h4>Financial Approval Required</h4>
                <p>This purchase order is pending financial approval. As a Financial Approver, you can approve or reject this PO.</p>
              </div>
              <button
                className="financial-approver-action-btn"
                onClick={() => setShowApprovalModal(true)}
              >
                <FontAwesomeIcon icon={faCheckCircle} />
                Approve / Reject
              </button>
            </div>
          </div>
        )}
        
        {/* Conversion — Request (operator). Shown while the PO is approved/
            received and not yet converted. */}
        {isEditMode && ['APPROVED', 'FULLY_RECEIVED'].includes(poStatus) && !poData.converted_to_incoming && (
          <div className="conversion-banner">
            <div className="conversion-banner-content">
              <div className="conversion-banner-icon">
                <FontAwesomeIcon icon={faExchangeAlt} />
              </div>
              <div className="conversion-banner-text">
                <h4>Convert to Incoming Invoice</h4>
                <p>This purchase order is approved. You can request conversion to an incoming invoice.</p>
              </div>
              <button
                className="conversion-action-btn"
                onClick={() => setShowConversionModal(true)}
              >
                <FontAwesomeIcon icon={faExchangeAlt} />
                Request Conversion
              </button>
            </div>
          </div>
        )}

        {/* Conversion — Approve/Reject (financial approver). Shown once a
            conversion has been requested and is pending a decision. */}
        {isEditMode && poStatus === 'PENDING_CONVERSION_APPROVAL' && user?.is_financial_approver && (
          <div className="conversion-banner">
            <div className="conversion-banner-content">
              <div className="conversion-banner-icon">
                <FontAwesomeIcon icon={faExchangeAlt} />
              </div>
              <div className="conversion-banner-text">
                <h4>Conversion Approval Required</h4>
                <p>This purchase order has been requested for conversion to an incoming invoice. As a Financial Approver, you can approve or reject it.</p>
              </div>
              <button
                className="conversion-action-btn"
                onClick={() => setShowConversionModal(true)}
              >
                <FontAwesomeIcon icon={faExchangeAlt} />
                Approve / Reject Conversion
              </button>
            </div>
          </div>
        )}

        {/* Conversion — Pending (non-approver). Informational only. */}
        {isEditMode && poStatus === 'PENDING_CONVERSION_APPROVAL' && !user?.is_financial_approver && (
          <div className="conversion-banner">
            <div className="conversion-banner-content">
              <div className="conversion-banner-icon">
                <FontAwesomeIcon icon={faExchangeAlt} />
              </div>
              <div className="conversion-banner-text">
                <h4>Conversion Requested</h4>
                <p>This purchase order has been submitted for conversion and is awaiting Financial Approver sign-off.</p>
              </div>
            </div>
          </div>
        )}
        
        {/* CRM cross-check flags (advisory — never blocks save/approval) */}
        {crmMatch && (
          <CrmMatchFlags checks={crmMatch.checks} overall={crmMatch.overall} />
        )}

        {/* Screen Content */}
        <div className="invoice-screens-container">
          {currentScreen === 1 && (
            <PurchaseOrderScreen1
              poData={poData}
              setPoData={setPoData}
              onNext={() => setCurrentScreen(2)}
              issuerName={documentIssuerName}
              isPersisted={Boolean(id)}
            />
          )}
          
          {currentScreen === 2 && (
            <PurchaseOrderScreen2
              poData={poData}
              setPoData={setPoData}
              lineItems={lineItems}
              setLineItems={setLineItems}
              vesselInfo={vesselInfo}
              setVesselInfo={setVesselInfo}
              onPrevious={() => setCurrentScreen(1)}
              onNext={() => setCurrentScreen(3)}
            />
          )}
          
          {currentScreen === 3 && (
            <PurchaseOrderScreen3
              poData={poData}
              setPoData={setPoData}
              lineItems={lineItems}
              vesselInfo={vesselInfo}
              onPrevious={() => setCurrentScreen(2)}
              isReadOnly={isReadOnly}
            />
          )}
        </div>
        </div>

        {documentId && (
          <DocumentsSplitPane
            isOpen={invoicePapers.isOpen}
            onClose={invoicePapers.close}
            title="Invoice Papers"
          >
            <RelatedDocumentsPane kind="purchase-order" invoiceId={documentId} />
          </DocumentsSplitPane>
        )}
        </div>
        
        {/* Footer */}
        <Footer />
      </div>
      
      {/* Financial Approval Modal */}
      {showApprovalModal && (
        <FinancialApprovalModal
          isOpen={showApprovalModal}
          onClose={() => setShowApprovalModal(false)}
          invoice={{
            ...poData,
            invoice_number: poData.po_number,
            invoice_date: poData.po_date,
            partner: poData.supplier,
            partner_name: poData.supplier_name,
            hms_entity_name: poData.hms_entity_name,
            status: poStatus,
            id: id
          }}
          isFinancialApprover={user?.is_financial_approver}
          onApprovalAction={handleFinancialApprovalAction}
        />
      )}
      
      {/* Conversion Approval Modal */}
      {showConversionModal && (
        <ConversionApprovalModal
          isOpen={showConversionModal}
          onClose={() => setShowConversionModal(false)}
          invoice={{
            ...poData,
            invoice_number: poData.po_number,
            invoice_date: poData.po_date,
            partner_name: poData.supplier_name,
            hms_entity_name: poData.hms_entity_name,
            gross_amount: poData.gross_amount,
            currency: poData.currency,
            converted_to_incoming: poData.converted_to_incoming,
            status: poStatus,
            id: id
          }}
          isFinancialApprover={user?.is_financial_approver}
          onConversionAction={handleConversionAction}
          sourceType="purchase_order"
        />
      )}
    </Layout>
  );
};

export default CreatePurchaseOrder;

